import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { setCategories } from '../store/slices/categoriesSlice';
import { selectAreCategoriesLoaded } from '../store/selectors/categoriesSelectors';
import {Theme} from "../config/theme";

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #f5f5f5;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  color: ${Theme.colors.main};
  font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
`;

const ReloadButton = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: #2980b9;
  }
`;

interface AppLoaderProps {
  children: React.ReactNode;
}

const AppLoader: React.FC<AppLoaderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const areCategoriesLoaded = useSelector(selectAreCategoriesLoaded);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch('/api/categories.json');
      if (!response.ok) {
        throw new Error('Failed to load categories');
      }
      const data = await response.json();
      dispatch(setCategories(data));
    } catch (err) {
      setError('Failed to load categories. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only load categories if they're not already in Redux
    if (!areCategoriesLoaded) {
      loadCategories();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areCategoriesLoaded]);

  if (error) {
    return (
      <LoadingContainer>
        <ErrorContainer>
          <LoadingText>{error}</LoadingText>
          <ReloadButton onClick={loadCategories}>Reload</ReloadButton>
        </ErrorContainer>
      </LoadingContainer>
    );
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading...</LoadingText>
      </LoadingContainer>
    );
  }

  return <>{children}</>;
};

export default AppLoader;
